<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">팝업 광고 신청 관리</h1>
          <p class="text-sm text-slate-500">파트너의 팝업 광고 신청을 승인/거절합니다</p>
        </div>
      </div>

      <!-- 필터 탭 -->
      <div class="flex gap-2 mb-6">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          @click="currentTab = tab.value"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition',
            currentTab === tab.value
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
        >
          {{ tab.label }}
          <span v-if="tab.value === 'pending' && pendingCount > 0" class="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
            {{ pendingCount }}
          </span>
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="filteredPopups.length === 0" class="bg-white rounded-2xl p-12 text-center">
        <svg class="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <h3 class="text-lg font-semibold text-slate-700 mb-2">
          {{ currentTab === 'pending' ? '대기 중인 신청이 없습니다' : '신청 내역이 없습니다' }}
        </h3>
      </div>

      <!-- 신청 목록 -->
      <div v-else class="space-y-4">
        <div
          v-for="popup in filteredPopups"
          :key="popup.id"
          class="bg-white rounded-2xl overflow-hidden shadow-sm"
        >
          <!-- 광고 미리보기 -->
          <div class="flex">
            <!-- 이미지 -->
            <div class="w-48 h-32 flex-shrink-0 relative">
              <img :src="popup.imageUrl" class="w-full h-full object-cover" />
              <div class="absolute top-2 left-2 px-2 py-0.5 text-xs font-bold bg-black/60 text-white rounded">AD</div>
            </div>
            <!-- 정보 -->
            <div class="flex-1 p-4">
              <div class="flex items-start justify-between">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span v-if="popup.badge" class="px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white shadow-md shadow-blue-600/25 rounded-full">
                      {{ popup.badge }}
                    </span>
                    <span :class="[
                      'px-2 py-0.5 text-xs font-medium rounded-full',
                      getStatusClass(popup.status)
                    ]">
                      {{ getStatusLabel(popup.status) }}
                    </span>
                  </div>
                  <h3 class="font-semibold text-slate-800" v-html="formatTitle(popup.title)"></h3>
                  <p v-if="popup.subtitle" class="text-sm text-slate-500 mt-1">{{ popup.subtitle }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 상세 정보 -->
          <div class="p-4 border-t border-slate-100">
            <div class="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <p class="text-slate-400">신청자</p>
                <p class="font-medium text-slate-700">{{ popup.partnerDisplayName }}</p>
              </div>
              <div>
                <p class="text-slate-400">현장</p>
                <p class="font-medium text-slate-700">{{ popup.propertyName }}</p>
              </div>
              <div>
                <p class="text-slate-400">캠페인 코드</p>
                <p class="font-medium text-slate-700">{{ popup.campaignCode }}</p>
              </div>
              <div>
                <p class="text-slate-400">노출 시간대</p>
                <p class="font-medium text-slate-700">
                  {{ popup.timeSlot?.label || '-' }}
                  <span v-if="popup.timeSlot" class="text-slate-500">({{ popup.timeSlot.start }}-{{ popup.timeSlot.end }})</span>
                </p>
              </div>
              <div>
                <p class="text-slate-400">신청일</p>
                <p class="font-medium text-slate-700">{{ formatDate(popup.createdAt) }}</p>
              </div>
            </div>

            <!-- 통계 (승인된 경우) -->
            <div v-if="popup.status === 'approved'" class="mt-4 p-3 bg-rose-50 rounded-xl">
              <div class="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p class="text-xl font-bold text-blue-600">{{ popup.impressions || 0 }}</p>
                  <p class="text-xs text-slate-500">노출</p>
                </div>
                <div>
                  <p class="text-xl font-bold text-blue-600">{{ popup.clicks || 0 }}</p>
                  <p class="text-xs text-slate-500">클릭</p>
                </div>
                <div>
                  <p class="text-xl font-bold text-blue-600">{{ getClickRate(popup) }}%</p>
                  <p class="text-xs text-slate-500">클릭률</p>
                </div>
              </div>
            </div>

            <!-- 거절 사유 (거절된 경우) -->
            <div v-if="popup.status === 'rejected' && popup.rejectReason" class="mt-4 p-3 bg-red-50 rounded-xl">
              <p class="text-sm text-red-600">
                <strong>거절 사유:</strong> {{ popup.rejectReason }}
              </p>
            </div>

            <!-- 승인/거절 버튼 (대기 중인 경우) -->
            <div v-if="popup.status === 'pending'" class="mt-4 pt-4 border-t border-slate-100 flex gap-3">
              <button
                @click="handleApprove(popup)"
                :disabled="processing === popup.id"
                class="flex-1 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {{ processing === popup.id ? '처리 중...' : '승인' }}
              </button>
              <button
                @click="openRejectModal(popup)"
                :disabled="processing === popup.id"
                class="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                거절
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 거절 사유 모달 -->
      <div v-if="showRejectModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-2xl p-6 w-full max-w-md">
          <h3 class="text-lg font-semibold text-slate-800 mb-4">거절 사유 입력</h3>
          <textarea
            v-model="rejectReason"
            placeholder="거절 사유를 입력해주세요..."
            rows="4"
            class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none resize-none"
          ></textarea>
          <div class="flex gap-3 mt-4">
            <button
              @click="showRejectModal = false"
              class="flex-1 py-2.5 bg-slate-100 text-slate-600 font-medium rounded-lg hover:bg-slate-200 transition"
            >
              취소
            </button>
            <button
              @click="handleReject"
              :disabled="!rejectReason.trim() || processing"
              class="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50"
            >
              {{ processing ? '처리 중...' : '거절하기' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { usePopupAd } from '@/composables/main/usePopupAd'

export default {
  name: 'PopupAdApplicationsView',
  components: {
    AdminLayout
  },
  setup() {
    const { getAllPopupAds, updatePopupAdStatus } = usePopupAd()

    const loading = ref(true)
    const popupAds = ref([])
    const currentTab = ref('pending')
    const processing = ref(null)
    const showRejectModal = ref(false)
    const rejectReason = ref('')
    const selectedPopup = ref(null)

    const tabs = [
      { label: '대기', value: 'pending' },
      { label: '승인', value: 'approved' },
      { label: '거절', value: 'rejected' },
      { label: '전체', value: 'all' }
    ]

    const filteredPopups = computed(() => {
      if (currentTab.value === 'all') return popupAds.value
      return popupAds.value.filter(p => p.status === currentTab.value)
    })

    const pendingCount = computed(() => {
      return popupAds.value.filter(p => p.status === 'pending').length
    })

    const loadPopupAds = async () => {
      loading.value = true
      try {
        popupAds.value = await getAllPopupAds()
      } catch (err) {
        console.error('팝업 광고 목록 로드 실패:', err)
      } finally {
        loading.value = false
      }
    }

    const getStatusLabel = (status) => {
      switch (status) {
        case 'pending': return '승인 대기'
        case 'approved': return '승인됨'
        case 'rejected': return '거절됨'
        default: return status
      }
    }

    const getStatusClass = (status) => {
      switch (status) {
        case 'pending': return 'bg-amber-100 text-amber-700'
        case 'approved': return 'bg-green-100 text-green-700'
        case 'rejected': return 'bg-red-100 text-red-600'
        default: return 'bg-slate-100 text-slate-600'
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    }

    const formatTitle = (text) => {
      if (!text) return ''
      return text.replace(/\n/g, '<br/>')
    }

    const getClickRate = (popup) => {
      if (!popup.impressions || popup.impressions === 0) return 0
      return ((popup.clicks || 0) / popup.impressions * 100).toFixed(1)
    }

    const handleApprove = async (popup) => {
      if (!confirm(`${popup.partnerDisplayName}의 팝업 광고를 승인하시겠습니까?`)) return

      processing.value = popup.id
      try {
        await updatePopupAdStatus(popup.id, 'approved')
        alert('승인되었습니다.')
        await loadPopupAds()
      } catch (err) {
        console.error('승인 실패:', err)
        alert('승인에 실패했습니다: ' + err.message)
      } finally {
        processing.value = null
      }
    }

    const openRejectModal = (popup) => {
      selectedPopup.value = popup
      rejectReason.value = ''
      showRejectModal.value = true
    }

    const handleReject = async () => {
      if (!selectedPopup.value || !rejectReason.value.trim()) return

      processing.value = selectedPopup.value.id
      try {
        await updatePopupAdStatus(selectedPopup.value.id, 'rejected', rejectReason.value.trim())
        alert('거절되었습니다.')
        showRejectModal.value = false
        await loadPopupAds()
      } catch (err) {
        console.error('거절 실패:', err)
        alert('거절에 실패했습니다: ' + err.message)
      } finally {
        processing.value = null
      }
    }

    onMounted(() => {
      loadPopupAds()
    })

    return {
      loading,
      popupAds,
      currentTab,
      tabs,
      filteredPopups,
      pendingCount,
      processing,
      showRejectModal,
      rejectReason,
      getStatusLabel,
      getStatusClass,
      formatDate,
      formatTitle,
      getClickRate,
      handleApprove,
      openRejectModal,
      handleReject
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";
</style>
